// src/services/payment/paymentReminderService.ts
//
// Service fuer das Versenden von Zahlungserinnerungen und das Nachladen
// der "letzte Erinnerung"-Information fuer die Offen-Liste im Zahlungsabgleich.
//
// Bewusst getrennt von paymentReconciliationService, weil:
//   - Reconciliation = Zahlungseingaenge zuordnen
//   - Reminder = ausgehende Mahn-Erinnerungen
// Sind zwei verschiedene fachliche Domaenen, beruehren sich nur in der UI.
//
// Mahnstufen (additiv gewaehlt, damit der Bestand unberuehrt bleibt):
//   level=1 = Zahlungserinnerung (wiederholbar)
//   level=2 = Letzte Erinnerung   (nicht wiederholbar, DB-Index erzwingt das)
//   level=3 = SEPA-Mahnung        (nicht wiederholbar; in der Drastik ZWISCHEN 1 und 2)
// Ablauf-Reihenfolge: 1 -> 3 -> 2.

import { supabase } from '../../shared/services/supabase';
import { netlifyFetch } from '../../shared/services/netlifyFetch';

// ============================================
// TYPES
// ============================================

export interface SendReminderResult {
  success: boolean;
  error?: string;
  sentTo?: string;
}

export interface LastReminderInfo {
  sent_at: string;
  level: number;
}

// Ein einzelner Mahnvorschlag (Feature halbautomatische Mahnvorschlaege).
export interface ReminderProposalItem {
  id: string;
  rechnungsnummer: string;
  empfaenger_firma: string;
  brutto_betrag: number;
  rechnungsdatum: string | null;
  tage_ueberfaellig: number;
  // Nur bei Level-3-Vorschlaegen relevant: Datum der ersten (aeltesten) Zahlungserinnerung.
  erste_erinnerung_am: string | null;
  // Nur bei Level-2-Vorschlaegen relevant: Datum der SEPA-Mahnung.
  sepa_mahnung_am: string | null;
}

// Die Vorschlagslisten auf dem aktuellen Stand (beim Öffnen des Zahlungsabgleichs und nach jedem CAMT-Import).
// level3 ist die SEPA-Mahnung, in der Drastik zwischen level1 und level2.
export interface ReminderProposals {
  level1: ReminderProposalItem[];
  level3: ReminderProposalItem[];
  level2: ReminderProposalItem[];
}

// ============================================
// HELPERS
// ============================================

/**
 * Kalendertage zwischen einem Datum und heute (jeweils auf Mitternacht normiert).
 * Liefert null bei leerem Input. Identische Logik wie daysSince in der View.
 */
function calendarDaysSince(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const today = new Date();
  d.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
}

// ============================================
// SERVICE
// ============================================

export const paymentReminderService = {

  /**
   * Triggert den Versand einer Mahnung fuer eine konkrete Rechnung.
   *
   * Die eigentliche Versand-Logik (Validierung, PDF-Anhang aus pdf_storage_path,
   * Mail-Versand, Logging in invoice_payment_reminders) sitzt in der Netlify-Function
   * /.netlify/functions/send-payment-reminder.
   *
   * @param invoiceId    UUID der Rechnung
   * @param senderEmail  E-Mail des eingeloggten XTAX-Mitarbeiters (fuer Audit-Log)
   * @param level        Mahnstufe: 1 = Zahlungserinnerung, 2 = Letzte Erinnerung, 3 = SEPA-Mahnung
   */
  sendPaymentReminder: async (
    invoiceId: string,
    senderEmail: string,
    level: 1 | 2 | 3
  ): Promise<SendReminderResult> => {
    try {
      const response = await netlifyFetch('/.netlify/functions/send-payment-reminder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_id: invoiceId,
          sender_email: senderEmail,
          level: level
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return {
          success: false,
          error: result.error || 'Versand der Mahnung fehlgeschlagen'
        };
      }

      return {
        success: true,
        sentTo: result.sentTo
      };
    } catch (error) {
      console.error('[PaymentReminder] Fehler beim Versand:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  /**
   * Laedt fuer eine Liste von Rechnungs-IDs jeweils die letzte versendete
   * Mahnung (Datum + Stufe). Wird im Zahlungsabgleich-Offen-Tab verwendet,
   * um die Sub-Zeile und die Button-Logik zu steuern.
   *
   * Implementierung bewusst als separater Query (kein PostgREST-Embed),
   * weil Embeds in unserer Codebase wiederholt unzuverlaessig waren.
   * Aggregat (max sent_at pro invoice_id) wird client-seitig gebildet.
   *
   * Da der Ablauf strikt 1 -> 3 -> 2 ist, spiegelt die juengste Stufe die
   * aktuelle Position der Rechnung wider (null / 1 / 3 / 2).
   *
   * @param invoiceIds  Liste der Rechnungs-UUIDs
   * @returns           Map<invoiceId, { sent_at, level }> der letzten Mahnung.
   *                    Rechnungen ohne Mahnung sind nicht in der Map enthalten.
   */
  getLastReminderForInvoices: async (
    invoiceIds: string[]
  ): Promise<Map<string, LastReminderInfo>> => {
    const map = new Map<string, LastReminderInfo>();
    if (invoiceIds.length === 0) return map;

    try {
      const { data, error } = await supabase
        .from('invoice_payment_reminders')
        .select('invoice_id, sent_at, level')
        .in('invoice_id', invoiceIds)
        .order('sent_at', { ascending: false })
        .limit(100000);

      if (error) {
        console.error('[PaymentReminder] Fehler beim Laden der Historie:', error);
        return map;
      }

      // Erste (juengste) Zeile pro invoice_id gewinnt
      for (const row of (data || []) as Array<{ invoice_id: string; sent_at: string; level: number }>) {
        if (!map.has(row.invoice_id)) {
          map.set(row.invoice_id, { sent_at: row.sent_at, level: row.level });
        }
      }
      return map;
    } catch (error) {
      console.error('[PaymentReminder] Unerwarteter Fehler:', error);
      return map;
    }
  },

  /**
   * Berechnet die halbautomatischen Mahnvorschlaege auf Basis des aktuellen
   * Stands (aufgerufen beim Öffnen des Zahlungsabgleichs und nach jedem CAMT-Import). Es werden
   * nur sendbare offene Rechnungen beruecksichtigt — exakt die Vorbedingungen
   * der Netlify-Function send-payment-reminder: Status freigegeben/versendet,
   * bezahlt_am IS NULL, nicht-Storno, Rechnungsnummer und Original-PDF vorhanden.
   *
   * Faelligkeit: Zahlungsziel ist "sofort", d.h. ueberfaellig ab Rechnungsdatum.
   * tage_ueberfaellig = Kalendertage seit rechnungsdatum.
   *
   * Listen (Reihenfolge im Ablauf: 1 -> 3 -> 2):
   *   - level1 (Zahlungserinnerung): tage_ueberfaellig >= 7 UND noch keine Mahnung vorhanden
   *   - level3 (SEPA-Mahnung): tage_ueberfaellig >= 14, Zahlungserinnerung liegt >= 7 Tage
   *             zurueck, noch keine SEPA-Mahnung und keine letzte Erinnerung, und der
   *             Kunde zahlt noch nicht per Lastschrift
   *   - level2 (Letzte Erinnerung): SEPA-Mahnung liegt >= 7 Tage zurueck UND noch keine
   *             letzte Erinnerung. Die letzte Erinnerung setzt damit immer eine
   *             vorausgegangene SEPA-Mahnung voraus.
   *
   * Die Reminder-Historie und die Zahlungsart der Kunden werden als separate
   * Queries geladen und client-seitig aggregiert (kein PostgREST-Embed).
   */
  getReminderProposals: async (): Promise<ReminderProposals> => {
    const empty: ReminderProposals = { level1: [], level3: [], level2: [] };
    try {
      const { data: invData, error: invError } = await supabase
        .from('invoices')
        .select('id, customer_id, rechnungsnummer, rechnungsdatum, brutto_betrag, empfaenger_firma')
        .is('bezahlt_am', null)
        .in('status', ['freigegeben', 'versendet'])
        .eq('ist_storno', false)
        .not('rechnungsnummer', 'is', null)
        .not('pdf_storage_path', 'is', null)
        .order('rechnungsdatum', { ascending: true })
        .limit(100000);

      if (invError) {
        console.error('[PaymentReminder] Fehler beim Laden offener Rechnungen fuer Vorschlaege:', invError);
        return empty;
      }

      const invoices = (invData || []) as Array<{
        id: string;
        customer_id: string | null;
        rechnungsnummer: string;
        rechnungsdatum: string | null;
        brutto_betrag: number | string;
        empfaenger_firma: string;
      }>;
      if (invoices.length === 0) return empty;

      // Zahlungsart der beteiligten Kunden laden (fuer den SEPA-Mahnung-Ausschluss
      // bereits per Lastschrift zahlender Kunden). Separater Query, client-seitig gemappt.
      const customerIds = Array.from(new Set(invoices.map(i => i.customer_id).filter((v): v is string => !!v)));
      const zahlungsartByCustomer = new Map<string, string | null>();
      if (customerIds.length > 0) {
        const { data: custData, error: custError } = await supabase
          .from('customers')
          .select('id, zahlungsart')
          .in('id', customerIds)
          .limit(100000);
        if (custError) {
          console.error('[PaymentReminder] Fehler beim Laden der Kunden-Zahlungsart fuer Vorschlaege:', custError);
          return empty;
        }
        for (const c of (custData || []) as Array<{ id: string; zahlungsart: string | null }>) {
          zahlungsartByCustomer.set(c.id, c.zahlungsart);
        }
      }

      // Komplette Mahn-Historie der betroffenen Rechnungen laden
      const invoiceIds = invoices.map(i => i.id);
      const { data: remData, error: remError } = await supabase
        .from('invoice_payment_reminders')
        .select('invoice_id, sent_at, level')
        .in('invoice_id', invoiceIds)
        .limit(100000);

      if (remError) {
        console.error('[PaymentReminder] Fehler beim Laden der Mahn-Historie fuer Vorschlaege:', remError);
        return empty;
      }

      // Pro Rechnung aggregieren: Gesamtanzahl, aelteste Level-1-Mahnung,
      // ob/wann Level 3 (SEPA-Mahnung), ob Level 2 existiert.
      const reminderCount = new Map<string, number>();
      const earliestLevel1 = new Map<string, string>();
      const level3Date = new Map<string, string>();
      const hasLevel2 = new Set<string>();
      for (const r of (remData || []) as Array<{ invoice_id: string; sent_at: string; level: number }>) {
        reminderCount.set(r.invoice_id, (reminderCount.get(r.invoice_id) || 0) + 1);
        if (r.level === 2) {
          hasLevel2.add(r.invoice_id);
        } else if (r.level === 1) {
          const cur = earliestLevel1.get(r.invoice_id);
          if (!cur || r.sent_at < cur) earliestLevel1.set(r.invoice_id, r.sent_at);
        } else if (r.level === 3) {
          // Pro Rechnung nur eine SEPA-Mahnung (DB-Index erzwingt das)
          level3Date.set(r.invoice_id, r.sent_at);
        }
      }

      const level1: ReminderProposalItem[] = [];
      const level3: ReminderProposalItem[] = [];
      const level2: ReminderProposalItem[] = [];

      for (const inv of invoices) {
        const tageUeberfaellig = calendarDaysSince(inv.rechnungsdatum);
        if (tageUeberfaellig === null) continue; // ohne Rechnungsdatum keine Faelligkeit berechenbar

        const count = reminderCount.get(inv.id) || 0;
        const l1Date = earliestLevel1.get(inv.id) || null;
        const l3Date = level3Date.get(inv.id) || null;
        const kundeZahlungsart = inv.customer_id ? (zahlungsartByCustomer.get(inv.customer_id) || null) : null;

        const item: ReminderProposalItem = {
          id: inv.id,
          rechnungsnummer: inv.rechnungsnummer,
          empfaenger_firma: inv.empfaenger_firma,
          brutto_betrag: Number(inv.brutto_betrag),
          rechnungsdatum: inv.rechnungsdatum,
          tage_ueberfaellig: tageUeberfaellig,
          erste_erinnerung_am: l1Date,
          sepa_mahnung_am: l3Date
        };

        // Level 1: >= 7 Tage ueberfaellig UND noch keine Mahnung
        if (count === 0 && tageUeberfaellig >= 7) {
          level1.push(item);
          continue;
        }

        // Level 3 (SEPA-Mahnung): Zahlungserinnerung vorhanden und >= 7 Tage her,
        // noch keine SEPA-Mahnung, noch keine letzte Erinnerung, >= 14 Tage ueberfaellig,
        // und Kunde zahlt noch nicht per Lastschrift.
        if (l1Date && !l3Date && !hasLevel2.has(inv.id) && tageUeberfaellig >= 14 && kundeZahlungsart !== 'lastschrift') {
          const daysSinceL1 = calendarDaysSince(l1Date);
          if (daysSinceL1 !== null && daysSinceL1 >= 7) {
            level3.push(item);
            continue;
          }
        }

        // Level 2 (Letzte Erinnerung): SEPA-Mahnung vorhanden und >= 7 Tage her,
        // noch keine letzte Erinnerung.
        if (l3Date && !hasLevel2.has(inv.id)) {
          const daysSinceL3 = calendarDaysSince(l3Date);
          if (daysSinceL3 !== null && daysSinceL3 >= 7) {
            level2.push(item);
          }
        }
      }

      return { level1, level3, level2 };
    } catch (error) {
      console.error('[PaymentReminder] Unerwarteter Fehler bei getReminderProposals:', error);
      return empty;
    }
  }
};
